import axios, { AxiosError } from 'axios';
import * as R from 'ramda';
import { CustomError, NetworkError, TechnicalError } from '../../common/errors';
import Result from '../../common/result';
import { Credentials } from '../../common/types';
import { Resolver } from '../../config';
import { Action, UserFlow } from '../../config/networkConfig';
import { registerRetryHandler } from '../common/networkRetries';
import NetworkSettable from '../common/networkSettable';
import { Token, TokenResult } from '../common/token';
import RetryPatchValidationHandler from './retryPatchValidationHandler';
import {
  CreateValidationRequest,
  CredentialsResult,
  DeleteValidationResult,
  FileUploadRequest,
  GetStepRequest,
  ProcessStepRequest,
  Step,
  StepResult,
  UploadResult,
  UserStatus,
  UserStatusRequest,
  UserStatusResult,
  Validation,
  ValidationRequest,
  ValidationResult,
  ValidationService,
} from './validationService';

type PassFlow = 'documentPass' | 'faceScan';
const issuancePassTypeMappings: Record<string, PassFlow> & Record<'default', PassFlow> = {
  default: 'documentPass',
  [UserFlow.UNIQUENESS]: 'faceScan',
  [UserFlow.LIVENESS]: 'faceScan',
  [UserFlow.FACE_AGE_CHECK_13]: 'faceScan',
};

const refreshPassTypeMappings: Record<string, PassFlow> & Record<'default', PassFlow> = {
  ...issuancePassTypeMappings,
  [UserFlow.DOCUMENT]: 'faceScan',
};

export default class HttpValidationService implements ValidationService, NetworkSettable {
  private credentialsCache: Record<string, Credentials> = {};

  public endpoint: string;

  public passType: PassFlow;

  public token?: Token;

  public defaultRetries: number;

  public customHeaders: Record<string, string>;

  constructor() {
    const serviceConfig = Resolver.getServiceConfig();
    this.endpoint = serviceConfig.validation.validationEndpoint;
    this.defaultRetries = serviceConfig.networkRetryConfig.defaultRetries;
    this.customHeaders = {};
    this.passType = issuancePassTypeMappings.default;
    registerRetryHandler(new RetryPatchValidationHandler(this));
  }

  setNetworkContext(network: string, action: Action): void {
    if (action === Action.REFRESH) {
      this.passType = refreshPassTypeMappings[network] || refreshPassTypeMappings.default;
    } else {
      this.passType = issuancePassTypeMappings[network] || issuancePassTypeMappings.default;
    }
  }

  setToken(token: Token): void {
    this.token = token;
  }

  setCustomHeaders(headers: Record<string, string>): void {
    this.customHeaders = R.clone(headers);
  }

  private async getToken(forPath: string): Promise<TokenResult> {
    if (!this.token) {
      return Result.failed(new TechnicalError(`Error calling ${forPath}: Missing auth token`));
    }

    return this.token.getToken();
  }

  async create(request: CreateValidationRequest): Promise<ValidationResult> {
    const url = `${this.endpoint}/validation/${this.passType}`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }
    try {
      return await axios
        .request<Validation>({
          method: 'POST',
          url,
          headers: R.mergeDeepLeft(
            {
              Authorization: `Bearer ${tokenResult.success()}`,
            },
            this.customHeaders,
          ),
          data: {
            networkAddress: request.networkAddress,
          },
          retryConfig: {
            retries: this.defaultRetries,
          },
        })
        .then((response) => Result.successful(response.data));
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  async getValidationResult(request: ValidationRequest): Promise<ValidationResult> {
    const url = `${this.endpoint}/validation/${this.passType}/${request.validationId}`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }

    try {
      return await axios
        .request<Validation>({
          method: 'GET',
          url,
          headers: R.mergeDeepLeft(
            {
              Authorization: `Bearer ${tokenResult.success()}`,
            },
            this.customHeaders,
          ),
          retryConfig: {
            retries: this.defaultRetries,
          },
        })
        .then((response) => Result.successful(response.data));
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  async deleteValidation(request: ValidationRequest): Promise<DeleteValidationResult> {
    const url = `${this.endpoint}/validation/${this.passType}/${request.validationId}`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }

    try {
      return await axios
        .request<Validation>({
          method: 'DELETE',
          url,
          headers: R.mergeDeepLeft(
            {
              Authorization: `Bearer ${tokenResult.success()}`,
            },
            this.customHeaders,
          ),
          retryConfig: {
            enableFailurePrompt: true,
          },
        })
        .then(() => Result.noValueSuccess());
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  async getNextStep(request: ValidationRequest): Promise<StepResult> {
    const url = `${this.endpoint}/validation/${this.passType}/${request.validationId}/steps/next`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }

    try {
      return await axios
        .request<Step>({
          method: 'GET',
          url,
          headers: R.mergeDeepLeft(
            {
              Authorization: `Bearer ${tokenResult.success()}`,
            },
            this.customHeaders,
          ),
          retryConfig: {
            retries: this.defaultRetries,
          },
        })
        .then((response) => Result.successful(response.data));
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  async getStep(request: GetStepRequest): Promise<StepResult> {
    const url = `${this.endpoint}/validation/${this.passType}/${request.validationId}/steps/${request.step}`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }
    try {
      return await axios
        .request<Step>({
          method: 'GET',
          url,
          headers: R.mergeDeepLeft(
            {
              Authorization: `Bearer ${tokenResult.success()}`,
            },
            this.customHeaders,
          ),
          retryConfig: {
            retries: this.defaultRetries,
          },
        })
        .then((response) => Result.successful(response.data));
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  async processStep(request: ProcessStepRequest): Promise<StepResult> {
    const url = `${this.endpoint}/validation/${this.passType}/${request.validationId}/steps/${request.step}`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }

    try {
      return await axios
        .request<Step>({
          method: 'PATCH',
          url,
          headers: R.mergeDeepLeft(
            {
              Authorization: `Bearer ${tokenResult.success()}`,
            },
            this.customHeaders,
          ),
          data: request.value,
          retryConfig: {
            retries: this.defaultRetries,
            validationId: request.validationId,
            validationStep: request.step,
          },
        })
        .then((response) => Result.successful(response.data));
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  /**
   * Convert base64 data to a binary payload
   *
   * @param base64Data - the base64 data to convert
   * @param contentType - the content type of the data
   * @param sliceSize - The number of characters to process per block
   * @returns - a binary data blob
   */
  private convertyBase64ToByteStream(base64Data: string, contentType: string, sliceSize = 512): Blob {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  async uploadFile(request: FileUploadRequest): Promise<UploadResult> {
    const url = request.uploadUrl;
    try {
      await axios.request({
        method: 'PUT',
        url,
        headers: R.mergeDeepLeft(
          {
            'Content-Type': 'application/octet-stream',
          },
          this.customHeaders,
        ),
        data: this.convertyBase64ToByteStream(request.base64FileData, request.contentType),
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      return Result.noValueSuccess();
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  async getStatus(request: UserStatusRequest): Promise<UserStatusResult> {
    const did = encodeURI(request.did);
    const url = `${this.endpoint}/validation/${this.passType}/status/${request.networkAddress}?did=${did}`;
    try {
      return await axios
        .request<UserStatus>({
          method: 'GET',
          url,
          headers: this.customHeaders,
        })
        .then((response) => Result.successful(response.data));
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  async getCredentials(request: ValidationRequest): Promise<CredentialsResult> {
    const url = `${this.endpoint}/validation/${this.passType}/${request.validationId}/credentials`;
    if (this.credentialsCache[url]) {
      return Result.successful(this.credentialsCache[url]);
    }
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }
    try {
      const response = await axios.request<Credentials>({
        method: 'GET',
        url,
        headers: R.mergeDeepLeft(
          {
            Authorization: `Bearer ${tokenResult.success()}`,
          },
          this.customHeaders,
        ),
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      // cache the credentials, if they exist
      if (response.data.credentials.length > 0) {
        this.credentialsCache[url] = response.data;
      }

      return Result.successful(response.data);
    } catch (error) {
      return HttpValidationService.getErrorResult(url, error);
    }
  }

  private static getErrorResult<TSuccess = boolean>(url: string, error: unknown): Result<CustomError, TSuccess> {
    const statusCode = (error as AxiosError)?.response?.status;
    return Result.failed(new NetworkError(`Network error calling ${url}`, statusCode || 500, error as Error));
  }
}
